
import { LiaFilePowerpoint } from "react-icons/lia";

import { BotAvatar } from '../BotAvatar';
import { ResourceDetails } from '../ResourceDetails';
import { FileResource } from '../resource-details/FileResource';
import { ListResource } from '../resource-details/ListResource';
import { LinkResource } from '../resource-details/LinkResource';
import { ChartResource } from '../resource-details/ChartResource';
import { TableResource } from '../resource-details/TableResource';
import { MapResource } from '../resource-details/MapResource';
import { MapAndImageResource } from '../resource-details/MapAndImageResource';
import { ReportResource } from '../resource-details/ReportResource';
import { ImageResource } from '../resource-details/ImageResource';
import { FloorplanResource } from '../resource-details/FloorplanResource';
import { LoD2Resource } from '../resource-details/LoD2Resource';
import Markdown from 'react-markdown';
import ChatTable from './ChatTable';
import remarkGfm from 'remark-gfm';
import { answerClipboardHtml, answerClipboardText } from '../utilities/answerClipboardHtml';
import rehypeRaw from 'rehype-raw';
import styles from './ChatMessage.module.sass';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';
import { userAPI } from '../UserAPI';
import { DownloadLink } from './DownloadLink';
import { ChatAgentSteps } from './ChatAgentSteps';
import truncate from 'lodash.truncate';
import { formatBerlin } from '../utils/berlinTime';
import { RESOURCE_CARD_CLASS } from '../resource-details/ResourceDisclosure';
import { isSourceResource, wholeWorkReferenceOf } from '../utils/sourceResource';
import { browserOpenUrl, opensInBrowser } from '../utils/sourceOpen';
import { openInPowerPoint, powerPointHref } from '../utils/officeLink';
import { collapseRunawayCharRuns } from '../utils/collapseRunawayCharRuns';
import { splitAgentStepSegments } from '../utils/agentStepSegments';
import { markdownLinkComponents, MarkdownLink } from './markdownLinkComponent';
import { GuideNavigateBlock } from '../guide/GuideNavigateBlock';
import { GuideQuestionBlock } from '../guide/GuideQuestionBlock';
import { GuideConfirmBlock } from '../guide/GuideConfirmBlock';
import { ConversationDocumentsContext, isViewableDocument, useDocumentViewer } from '@/documentViewer/DocumentViewerContext';
import { rehypeStripModelStyling } from '../utils/rehypeStripModelStyling';
import { CITATION_CLASS, rehypeDocumentCitations } from '@/documentViewer/rehypeDocumentCitations';

import { Check, Code } from "lucide-react";
import { Message, MessageHeader } from "@/components/agents/message";
import type { CitationItem } from "@/components/agents/citations";
import { StreamingResponse } from "@/components/agents/streaming-response";
import { CodeBlock } from "@/components/agents/code-block";
import { MessageBubble, MessageBubbleContent } from "@/components/agents/message-bubble";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/** Ersatz für den Fluent-`TooltipHost`: gleiches Aufrufmuster (content + Kinder). */
function TooltipHost({ content, children }: { content: React.ReactNode; children: React.ReactNode }) {
    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex">{children}</span>
                </TooltipTrigger>
                {/* Schriftgrad wie im Tooltip der eingeklappten Sidebar. */}
                <TooltipContent className='text-sm font-medium'>{content}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

interface ChatMessageProps {
    message: any;
    index: number;
    isLastMessage: boolean;
    pendingRequest: boolean;
    onSubmitInput: (input: string, prompt?: string, options?: { exclusiveToolId?: string; onAccepted?: () => void }) => void;
    onFeedback: (index: number, isPositive: boolean) => void;
    feedbackValue?: 'good' | 'bad';
    resourceSelected?: (resource: any) => void;
    /**
     * Diese Zeile ist *während* der Sitzung entstanden und blendet sich einmal
     * ein (`Message`/`MessageBubble`). Der Verlauf einer alten Unterhaltung
     * steht dagegen still — siehe die Baseline-Logik in `ChatConversation`.
     */
    animateIn?: boolean;
    /**
     * Hide the per-message action bar — the sources toggle, the thumbs, the copy buttons.
     *
     * Set where the conversation is embedded rather than the page's subject: the tender board shows
     * it inside a row that already carries the tender's own actions, and a second row of controls
     * per message competes with them for the reader's attention. The chat page leaves it on.
     */
    hideMessageActions?: boolean;
    /**
     * The compact action bar: **thumbs and the secondary button are dropped**;
     * copy and sources stay. The name says what the flag does — it carries two
     * concerns because both share the same reason.
     *
     * Set by the question panel (`OverlayBotChat`), which uses the same
     * renderer: a thumb needs a conversation id **and** a message index to
     * reach the server; the panel carries neither. A button that does nothing
     * is worse than none. And the secondary button „als HTML kopieren" would
     * sit next to the first in the narrow column, with no way to tell the
     * difference without an explanation.
     */
    compactActions?: boolean;
}

export const ChatMessage = ({ message, index, isLastMessage, pendingRequest, onSubmitInput, onFeedback, feedbackValue, resourceSelected, animateIn = false, hideMessageActions, compactActions }: ChatMessageProps) => {
    const { t } = useTranslation();
    // Die Nachricht ist monochrom: Flächen und Schrift kommen aus der
    // Opazitätsleiter (`--oa-*`), nicht mehr aus der Kundenfarbe.
    const [authentication] = useAtom(authenticationAtom) as any;
    const [copyHtmlSuccess, setCopyHtmlSuccess] = useState(false);
    const [userMessageExpanded, setUserMessageExpanded] = useState(false);
    // Quellen öffnen im Dokumentbetrachter neben dem Chat, wenn es ihn gibt (siehe DocumentViewerContext).
    const documentViewer = useDocumentViewer();
    const conversationDocuments = React.useContext(ConversationDocumentsContext);
    const rehypePlugins = React.useMemo(
        () => [rehypeRaw, rehypeStripModelStyling, [rehypeDocumentCitations, { documents: conversationDocuments }]] as any,
        [conversationDocuments]
    );


    /** Kopiert wird die Antwort, nicht der Weg dorthin: Schrittblöcke fliegen
     *  aus dem **Rohtext** der Nachricht. */
    const stripProgressDivs = (content: string) => {
        return content.replace(/<div\s+class=["']progress["'][^>]*>.*?<\/div>/gis, '').trim();
    };

    /**
     * Kopieren ohne Clipboard-API (kein sicherer Kontext, z. B. http auf erich): Inhalt im
     * `copy`-Ereignis setzen. Ein verstecktes Textfeld markieren, wie bis 09.10.2026, kopierte
     * nichts, wenn das Feld den Fokus nicht bekam — die Zwischenablage behielt den vorigen Inhalt
     * (gemeldet: beide Symbole lieferten HTML).
     */
    const copyViaEvent = (data: Record<string, string>): boolean => {
        const onCopy = (event: ClipboardEvent) => {
            for (const [type, value] of Object.entries(data)) event.clipboardData?.setData(type, value);
            event.preventDefault();
        };
        document.addEventListener('copy', onCopy);
        try {
            return document.execCommand('copy');
        } finally {
            document.removeEventListener('copy', onCopy);
        }
    };

    const fallbackCopyText = async (text: string) => {
        if (!copyViaEvent({ 'text/plain': text })) throw new Error('execCommand copy failed');
    };

    /**
     * Die Antwort als Text in die Zwischenablage.
     *
     * Bis 09.10.2026 zuerst über `ClipboardItem` mit `text/markdown` — ein Format, das Chrome für die
     * Zwischenablage nicht annimmt: der Aufruf schlug fehl, und der Rückfall über ein verstecktes
     * Textfeld kopierte, wenn das Feld den Fokus nicht bekam (Fokus-Falle eines Dialogs), die
     * aktuelle Auswahl — gemeldet wurde, dass „Kopieren" am Ende der Antwort die Frage kopierte.
     * Jetzt `writeText` (überall unterstützt), der Rückfall nur, wenn es ihn nicht gibt.
     */
    const handleCopyMessage = async () => {
        const markdownContent = stripProgressDivs(message.content || '');
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(markdownContent);
                return;
            }
            await fallbackCopyText(markdownContent);
        } catch (err) {
            try {
                await fallbackCopyText(markdownContent);
            } catch (fallbackErr) {
                console.error('Failed to copy message:', err, fallbackErr);
            }
        }
    };

    /**
     * Die Antwort als formatierter Text (HTML) — zum Einfügen in Word.
     *
     * Bis 09.10.2026 suchte die Funktion ihre Antwortfläche per `document.querySelector` über
     * `data-message-index` und meldete auch dann „kopiert", wenn sie nichts fand oder das Schreiben
     * scheiterte: Die Zwischenablage behielt den alten Inhalt (das Markdown des anderen Symbols, oder
     * die vorher kopierte Frage). Danach kopierte sie das `innerHTML` der Darstellung — mit Klassen
     * und Design-Hintergründen, Word zeigte graue Kästen. Jetzt sauberes HTML aus dem Markdown
     * (`answerClipboardHtml`), `ClipboardItem` mit `text/html` + `text/plain`, als Rückfall eine
     * Auswahl dieses HTML mit `execCommand('copy')`. „Kopiert" nur, wenn es geklappt hat.
     */
    const handleCopyHtml = async () => {
        // Aus dem Markdown neu erzeugt, nicht aus der Darstellung — siehe answerClipboardHtml.
        const htmlContent = answerClipboardHtml(message.content || '');
        const plainTextContent = answerClipboardText(message.content || '');
        const confirm = () => {
            setCopyHtmlSuccess(true);
            setTimeout(() => setCopyHtmlSuccess(false), 2000);
        };
        try {
            if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([htmlContent], { type: 'text/html' }),
                        'text/plain': new Blob([plainTextContent], { type: 'text/plain' }),
                    }),
                ]);
                confirm();
                return;
            }
        } catch (err) {
            console.warn('ClipboardItem failed, falling back to a selection copy:', err);
        }
        /*
         * Rückfall ohne Clipboard-API (kein sicherer Kontext, z. B. http auf erich): Inhalt im
         * `copy`-Ereignis setzen. Bis 09.10.2026 wurde hier ein Knoten in die Seite gehängt und
         * markiert — Chrome schrieb dabei die berechneten Stile der Portal-Seite mit, und Word
         * zeigte wieder graue Kästen. Über `clipboardData` geht genau dieses HTML hinein.
         */
        try {
            if (copyViaEvent({ 'text/html': htmlContent, 'text/plain': plainTextContent })) confirm();
            else console.error('Failed to copy HTML: execCommand returned false');
        } catch (err) {
            console.error('Failed to copy HTML:', err);
        }
    };

    const renderSourceDocument = (source: any) => {
        const url = source.url && source.url.startsWith('http') ? source.url : userAPI.getResourceURL(source.url || '');
        const rangeToString = (start: number, end: number, separator = ', '): string => {
            if (start > end) return '';
            const numbers = [];
            for (let i = start; i <= end; i++) {
                numbers.push(i);
            }
            return numbers.join(separator);
        }
        const buildPagesUrl = (resourceUrl: string, queryPages: string): string => {
            if (!queryPages) {
                return resourceUrl;
            }
            const separator = resourceUrl.includes('?') ? '&' : '?';
            return `${resourceUrl}${separator}pages=${encodeURIComponent(queryPages)}`;
        };
        const fileExtension = source.url?.split('.').pop();
        const isPowerPoint = fileExtension === 'pptx' || fileExtension === 'ppt';
        const pages = rangeToString(source.startpage, source.endpage);
        const pagesQuery = rangeToString(source.startpage, source.endpage, ',');
        const pagesUrl = buildPagesUrl(url, pagesQuery);
        // Was der Browser selbst darstellt, wird geöffnet statt heruntergeladen — dasselbe, was der
        // Dokumentbetrachter und die Dokumentmenüs im Chat überall sonst tun. Ein PDF geht dabei
        // gleich auf die Fundstelle. Alles andere (docx, xlsx, csv) bleibt beim Download: ein neuer
        // Tab lieferte dort nur einen Download ohne Namen oder eine leere Seite.
        const openable = !!source.url && opensInBrowser(source.url);
        // Eine Quelle mit Datei-Id als PDF oder Text (die Normtexte der Regelwerke sind `.md`) öffnet
        // im Dokumentbetrachter an der Seite — authentifiziert und im Portal, statt in einem neuen Tab
        // über den offenen `/documents`-Pfad.
        const inViewer =
            !!documentViewer &&
            !!source.documentId &&
            isViewableDocument(String(source.url || source.document || '').split(/[?#]/)[0]);
        const openLink = (target: string, text: React.ReactNode, page?: number) => inViewer ? (
            <button
                type="button"
                onClick={() => documentViewer!.openDocument({
                    fileId: String(source.documentId),
                    filename: source.document,
                    page: page ?? source.startpage,
                    reference: wholeWorkReferenceOf(source, sourceResources),
                })}
                data-testid="chat-source-open"
                className="inline-flex cursor-pointer items-center gap-1.5 text-left underline-offset-2 hover:underline"
            >
                {text}
            </button>
        ) : (
            <a
                href={target}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-source-open"
                className="inline-flex cursor-pointer items-center gap-1.5"
            >
                {text}
            </a>
        );
        // Inhalt einer Quellenzeile. Das Symbol links und der Anbieter rechts
        // kommen von `CitationList`; hier steht nur, was die Quelle selbst ist.
        return <>
            {!!source.url ?
            <>
                {openable ? (
                    source.summary ? (
                        <TooltipHost content={source.summary}>
                            {openLink(browserOpenUrl(url, source.startpage), source.document)}
                        </TooltipHost>
                    ) : (
                        openLink(browserOpenUrl(url, source.startpage), source.document)
                    )
                ) : source.summary ? (
                    <TooltipHost content={source.summary}>
                        <DownloadLink url={url} text={source.document} saveToFileSystem={true} />
                    </TooltipHost>
                ) : (
                    <DownloadLink url={url} text={source.document} saveToFileSystem={true} />
                )}
                {isPowerPoint && <a href={powerPointHref(url)} onClick={(event) => { event.preventDefault(); void openInPowerPoint(url); }} data-testid="chat-source-powerpoint">
                    <LiaFilePowerpoint className='align-middle text-xs text-current' />
                </a>}
                {openable ? (
                    // Jede Fundseite einzeln: der Klick geht direkt auf die Seite im geöffneten
                    // Dokument, statt einen Seitenausschnitt herunterzuladen.
                    pages && (
                        <span className='-ml-1'>
                            ({rangeToString(source.startpage, source.endpage).split(', ').map((page: string, index: number, all: string[]) => (
                                <React.Fragment key={page}>
                                    {openLink(browserOpenUrl(url, Number(page)), page, Number(page))}
                                    {index < all.length - 1 ? ', ' : ''}
                                </React.Fragment>
                            ))})
                        </span>
                    )
                ) : (source.sourceType === "file") ?
                    pages && (
                    <span className='-ml-1'>
                        ({source.summary ? (
                            <TooltipHost content={source.summary}>
                                <DownloadLink url={pagesUrl} text={pages} saveToFileSystem={true} />
                            </TooltipHost>
                        ) : (
                            <DownloadLink url={pagesUrl} text={pages} saveToFileSystem={true} />
                        )}
                        {isPowerPoint && <a href={powerPointHref(pagesUrl)} onClick={(event) => { event.preventDefault(); void openInPowerPoint(pagesUrl); }} data-testid="chat-source-powerpoint">
                            <LiaFilePowerpoint className='align-middle text-xs text-current' />
                        </a>})
                    </span>
                    )
                    : pages && <span className='-ml-1'>({pages})</span>}
            </> : <>
                {source.document}{pages && <span className='-ml-1'> ({pages})</span>}
            </>
            }
        </>
    }

    // Custom Markdown component for inline resources - moved outside to use useMemo
    const markdownComponents = React.useMemo(() => ({
        // External links leave the portal, so they must not replace the chat in
        // this tab. Internal links (e.g. the guide's [API-Schlüssel](/settings?...))
        // navigate in place instead of reloading the bundle; mailto:/tel:/#anchor
        // keep default handling. Anything else — including addresses that only
        // look internal, such as /\evil.example — is rendered without an href,
        // because this markdown comes from model and tool output and
        // rehypeRaw below lets it carry raw anchors. See markdownLinkComponent.tsx.
        ...markdownLinkComponents,
        // A table rolls in its own box with a visible scrollbar, columns keep a readable minimum
        // width, and it can go fullscreen, to the clipboard (for Excel) or out as CSV. See ChatTable.
        table: ChatTable,
        // Fenced code gets a copy button in its corner.
        pre: ({ node, ...props }: any) => (
            <CodeBlock labels={{ copy: t('global.copy'), copied: t('global.copied') }} {...props} />
        ),
        // The guide's text mirrors its `navigate` block as a markdown link (mail and app need
        // it). In the portal the block is a button right below, so the same link in the prose
        // is a duplicate and is dropped (28.09.2026). Other links stay.
        a: (props: any) => {
            const navigateRoute = message.resources?.find((r: any) => r?.type === 'navigate')?.data?.route;
            if (navigateRoute && typeof props.href === 'string' && props.href === navigateRoute) return null;
            return <MarkdownLink {...props} />;
        },
        // Eine Dokumentnennung in der Antwort (rehypeDocumentCitations) öffnet den Betrachter auf der
        // genannten Seite; ohne Betrachter bleibt sie Text.
        span: ({ node, className, children, ...props }: any) => {
            if (className === CITATION_CLASS && documentViewer && props['data-file-id']) {
                const fileId = String(props['data-file-id']);
                const page = Number(props['data-page']) || undefined;
                const known = conversationDocuments.find(doc => doc.fileId === fileId);
                return (
                    <button
                        type="button"
                        className="doc-cite inline cursor-pointer text-left text-primary underline decoration-dotted underline-offset-2 hover:decoration-solid"
                        data-testid="chat-document-citation"
                        title={t('documentViewer.openCitation')}
                        onClick={() => documentViewer.openDocument({
                            fileId,
                            filename: props['data-file-name'] || known?.filename,
                            page,
                            webUrl: known?.webUrl,
                        })}
                    >
                        {children}
                    </button>
                );
            }
            return <span className={className} {...props}>{children}</span>;
        },
        div: ({ node, ...props }: any) => {
            // Check if this is an inline resource placeholder
            if (props.className === 'inline-resource' && props['data-resource-id']) {
                const resourceId = props['data-resource-id'];
                const resource = message.resources?.find((r: any) => 
                    (r._stableId || r.reference || r._id) === resourceId
                );
                
                if (resource) {
                    // Skip file resources from inline rendering - they should only be rendered at the bottom
                    if (resource.type === 'file') {
                        console.log(`📄 Skipping inline rendering of file resource: ${resourceId} - file resources should only be rendered at the bottom`);
                        return <div key={resourceId} className='my-2 rounded-sm border border-dashed border-border bg-muted p-2 text-xs text-muted-foreground'>
                            📄 File: {resource.filename || resource.name || 'Document'} (rendered below)
                        </div>;
                    }
                    
                    // A portal guide block is not a tile but a button: the
                    // rounded card plus the break-out from the bubble
                    // (`margin: 16px -12px`) would put a pill button into a
                    // screen-wide area. So it only gets spacing — it is
                    // rendered like any other type in the `switch`.
                    // Guide blocks draw themselves — no resource frame around them.
                    const isBareBlock = resource.type === 'navigate' || resource.type === 'question' || resource.type === 'confirm';

                    // Rendering inline resource
                    // Render the resource inline
                    return (
                        // Eine Kachel mitten im Text steht allein — sie bekommt
                        // die runde Karte hier, die Gruppe unter der Antwort
                        // bringt sie sich selbst mit.
                        <div key={resourceId} className={isBareBlock ? undefined : RESOURCE_CARD_CLASS} style={isBareBlock ? undefined : {
                            margin: '16px -12px', // Break out of bubble padding (12px)
                            width: 'calc(100% + 24px)', // Expand to full bubble width
                            boxSizing: 'border-box'
                        }}>
                            {(() => {
                                switch (resource.type) {
                                    case 'chart': 
                                        return <ChartResource key={resourceId} resource={resource} stableKey={resourceId} resourceSelected={resourceSelected}></ChartResource>
                                    case 'table': 
                                        return <TableResource key={resourceId} resource={resource} stableKey={resourceId}></TableResource>
                                    case 'map': 
                                        return <MapResource key={resourceId} resource={resource} stableKey={resourceId} resourceSelected={resourceSelected}></MapResource>
                                    case 'mapAndImage': 
                                        return <MapAndImageResource key={resourceId} resource={resource} stableKey={resourceId} resourceSelected={resourceSelected}></MapAndImageResource>
                                    case 'image': 
                                        return <ImageResource key={resourceId} resource={resource} stableKey={resourceId}></ImageResource>
                                    case 'list': 
                                        return <ListResource key={resourceId} resource={resource} stableKey={resourceId}></ListResource>
                                    case 'link': 
                                        return <LinkResource key={resourceId} resource={resource} stableKey={resourceId}></LinkResource>
                                    case 'lod2View': 
                                        return <LoD2Resource key={resourceId} resource={resource} stableKey={resourceId}></LoD2Resource>
                                    case 'report': 
                                        return <ReportResource key={resourceId} resource={resource} stableKey={resourceId}></ReportResource>
                                    case 'floorplan':
                                        return <FloorplanResource key={resourceId} resource={resource} stableKey={resourceId}></FloorplanResource>
                                    // Portal guide, stage 2: the route as a button in
                                    // house style — an anchor, so right-click and
                                    // ⌘-click keep working (`GuideNavigateBlock`). It
                                    // lives here because the `default` below would
                                    // show "Unknown resource type" in English.
                                    case 'navigate':
                                        return <GuideNavigateBlock key={resourceId} resource={resource} />
                                    // Stage 3: the follow-up question with buttons — a click becomes a message.
                                    case 'question':
                                        return <GuideQuestionBlock key={resourceId} resource={resource} collapsed={!isLastMessage} />
                                    // Stage 3: the confirmation card — [Ja]/[Nein] go to the approval route.
                                    case 'confirm':
                                        return <GuideConfirmBlock key={resourceId} resource={resource} collapsed={!isLastMessage} />
                                    default:
                                        return <div key={resourceId}>Unknown resource type: {resource.type}</div>;
                                }
                            })()}
                        </div>
                    );
                } else {
                    console.log(`❌ Resource not found for ID: ${resourceId}. Available resources:`, message.resources?.map((r: any) => ({ id: r.reference || r._id, type: r.type })));
                    return <div key={resourceId} className='border border-dashed border-destructive p-2 text-destructive'>
                        Resource not found for ID: {resourceId}
                    </div>;
                }
            }
            
            // Default div rendering
            return <div {...props} />;
        }
    }), [message.resources, resourceSelected, isLastMessage, documentViewer, conversationDocuments, t]);

    // The answer being written right now: streamed text shows as it arrives.
    const answerStreaming = pendingRequest && isLastMessage;
    // Tiles, files and guide cards wait until the answer is complete: dropped
    // in mid-stream they shove the text that is still being written. Whatever
    // was held back animates in when it finally shows.
    const holdResources = answerStreaming;
    const heldResourcesRef = React.useRef(false);
    if (holdResources) heldResourcesRef.current = true;
    const animateResources = animateIn || heldResourcesRef.current;
    // Older stored messages can contain a runaway single-character run that
    // react-markdown turns into one unbreakable word, freezing the browser.
    const safeContent = React.useMemo(
        () => collapseRunawayCharRuns(message.content || ''),
        [message.content]
    );

    // Die agentischen Schritte (`<div class="progress">` im gestreamten
    // Markdown) werden **vor** dem Rendern herausgelöst, damit aufeinander-
    // folgende Schritte als eine Gruppe im Card-Rahmen erscheinen. Alles
    // dazwischen bleibt Markdown und geht weiter durch react-markdown.
    const segments = React.useMemo(() => splitAgentStepSegments(safeContent), [safeContent]);
    const lastStepsSegment = segments.reduce(
        (last, segment, i) => (segment.kind === 'steps' ? i : last),
        -1
    );
    // Läuft der Agent noch, ist der letzte Schritt der laufende — aber nur in
    // der Nachricht, an der gerade gearbeitet wird.
    const stepsRunning = pendingRequest && isLastMessage;

    // Guide cards (navigate, question, confirm) that carry no inline placeholder — the persisted
    // history never has one — sit between the answer text and the action bar, not under it:
    // they are part of the answer, not an attachment. Everything else keeps its place below.
    const GUIDE_CARD_TYPES = new Set(['navigate', 'question', 'confirm']);
    const isRenderedInline = (resource: any) => {
        const resourceId = resource.reference || resource._id;
        return !!resourceId && !!message.content?.includes(`data-resource-id="${resourceId}"`);
    };
    const guideCardsAfterText = (message.resources || []).filter(
        (resource: any) => GUIDE_CARD_TYPES.has(resource.type) && !isRenderedInline(resource)
    );

    // Spaltenvertrag der Antwort, den auch die Ressourcen unter ihr einhalten:
    // dieselbe Zeile (30px Seitenrand) und dieselbe Breite. Die Antwort steht
    // ohne Avatar und ohne Sprechblase auf dem Grund der Seite, füllt die Spalte
    // also ganz aus — eine 80%-Kappung bliebe nur für die Nutzer-Blase sinnvoll.
    // `mt-3` setzt die Kacheln etwas tiefer als die Aktionsleiste der Antwort:
    // Antwort und Ergebnisse sollen als zwei Blöcke lesbar sein, nicht als einer.
    const resourceRowClass = 'mt-3 flex items-start overflow-x-hidden px-[30px] max-sm:px-1';
    const resourceColumnClass = 'min-w-0 w-full grow';

    // Quellen der Antwort: die Quellen-Ressourcen der Nachricht und das eigene
    // Feld `sourceResources` zusammengelegt, doppelte Einträge fallen weg.
    const mergedSourceResources = [
        ...(message.resources ? message.resources.filter(isSourceResource) : []),
        ...(message.sourceResources || []),
    ];
    const seenSourceKeys = new Set<string>();
    const sourceResources = mergedSourceResources.filter((r: any) => {
        const key = `${r.reference ?? ''}|${r.url ?? ''}|${r.tool ?? ''}|${r.startpage ?? ''}|${r.endpage ?? ''}`;
        if (seenSourceKeys.has(key)) return false;
        seenSourceKeys.add(key);
        return true;
    });

    /** Die Quellen als Zitate der Antwort — nummeriert, mit Anbieter als Herkunft. */
    const sourceCitations: CitationItem[] = sourceResources.map((source: any, idx: number) => {
        const id = `${idx}-${source.reference ?? ''}|${source.url ?? ''}|${source.tool ?? ''}|${source.startpage ?? ''}|${source.endpage ?? ''}`;
        if (source.document) {
            // Dokumente behalten ihren Download-Link samt Seitenbereich. Der Titel
            // trägt den Link, die Zeile selbst bekommt deshalb keine URL: ein Link
            // im Link wäre ungültiges HTML und verschluckt den inneren Klick.
            return { id, title: renderSourceDocument(source), domain: source.provider };
        }
        const url = source.url && source.url.startsWith('http')
            ? source.url
            : userAPI.getResourceURL(source.url || '');
        return {
            id,
            title: source.summary ? <span title={source.summary}>{source.tool}</span> : source.tool,
            domain: source.provider,
            url,
        };
    });

    /** „Als HTML kopieren" gibt es in der Vorlage nicht; der Knopf reiht sich
     *  hinter den eingebauten Aktionen ein und trägt deren Optik. */
    const copyHtmlAction = (
        <TooltipHost content={copyHtmlSuccess ? t('global.copied') : t('global.copyHtml')}>
            <button
                type='button'
                data-testid='chat-message-copy-html'
                aria-label={t('global.copyHtml')}
                onClick={handleCopyHtml}
                disabled={pendingRequest}
                className='grid size-7 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50'
            >
                {copyHtmlSuccess ? <Check className='size-3.5' /> : <Code className='size-3.5' />}
            </button>
        </TooltipHost>
    );

    switch (message.type) {
        case 'botMessage':
            return (message.content && message.content.length > 0) ||
                (message.resources && message.resources.length > 0)
                ?
                <Message
                    from='assistant'
                    animateIn={animateIn}
                    data-message-index={index}
                    data-pending-request={pendingRequest ? "true" : "false"}
                    // Die Antwort ist keine Blase, sondern eine volle Spalte:
                    // die Zeilen-Flexbox von `Message` wird hier auf `block`
                    // zurückgenommen, die Einblende-Animation bleibt.
                    className='block w-full'
                >
                    {/* Die Antwort steht ohne Avatar und ohne Sprechblase auf dem
                        Grund der Seite und nutzt die Spalte ganz aus; nur die
                        Frage darüber bleibt eine Blase. */}
                    <div className='flex items-start overflow-x-hidden px-[30px] max-sm:px-1'>
                        <div className='relative flex w-full min-w-0 grow flex-col items-start overflow-x-hidden'>
                            <MessageHeader className='mb-1 min-h-[15px] px-0 text-[10px] leading-normal text-[var(--oa-fg-4)]'>
                                {message.date ? (
                                    <>
                                        {formatBerlin(message.date, t('global.dateShort'))}{' '}
                                        {formatBerlin(message.date, 'HH:mm')}
                                    </>
                                ) : (
                                    <span className='opacity-0' aria-hidden>
                                        &nbsp;
                                    </span>
                                )}
                            </MessageHeader>
                            {/* Die Antwortfläche: der gestreamte Inhalt, darunter die
                                Aktionen und die Quellen — sie erscheinen erst, wenn
                                die Antwort steht. */}
                            <StreamingResponse
                                status={answerStreaming ? 'streaming' : 'complete'}
                                onCopy={handleCopyMessage}
                                copyText={stripProgressDivs(message.content || '')}
                                sources={sourceCitations}
                                sourceIdPrefix={`chat-source-${index}`}
                                feedback={feedbackValue === 'good' ? 'up' : feedbackValue === 'bad' ? 'down' : null}
                                // Ein zweiter Klick auf denselben Daumen nimmt die
                                // Bewertung in der Vorlage zurück; das Portal kennt
                                // dafür keinen Weg, also bleibt sie stehen.
                                onFeedbackChange={(next) => { if (next) onFeedback(index, next === 'up'); }}
                                showActions={!hideMessageActions}
                                showFeedback={!compactActions}
                                actionsDisabled={pendingRequest}
                                announce={pendingRequest && isLastMessage}
                                extraActions={hideMessageActions || compactActions ? null : copyHtmlAction}
                                labels={{
                                    copy: t('global.copyMarkdown'),
                                    copied: t('global.copied'),
                                    helpful: t('global.goodAnswer'),
                                    notHelpful: t('global.notSoGoodAnswer'),
                                    source: t('chatMessages.source'),
                                    sources: t('chatMessages.sources'),
                                    showSources: t('chatMessages.showSources'),
                                    hideSources: t('chatMessages.hideSources'),
                                }}
                                testIds={{
                                    actions: 'chat-message-actions',
                                    copy: 'chat-message-copy-markdown',
                                    helpful: 'chat-message-feedback-good',
                                    notHelpful: 'chat-message-feedback-bad',
                                    sources: 'chat-message-sources-toggle',
                                }}
                                className='w-full'
                                contentClassName='w-full min-w-0'
                                sourcesClassName={styles.sourcesList}
                            >
                                <div
                                    className={`markdown ${styles.markdown} w-full`}
                                    style={{
                                        // Keep horizontal scrolling for wide markdown (code blocks/tables/URLs).
                                        // Lists use list-style-position: inside, so markers are not clipped.
                                        overflowX: 'auto',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none'
                                    }}
                                >
                                    <style>{`.chatBot .markdown::-webkit-scrollbar { display: none; }`}</style>
                                    {segments.map((segment, segmentIndex) => (
                                        segment.kind === 'steps'
                                            ? <ChatAgentSteps
                                                key={`steps-${segmentIndex}`}
                                                steps={segment.steps}
                                                running={stepsRunning && segmentIndex === lastStepsSegment}
                                            />
                                            : <Markdown
                                                key={`markdown-${segmentIndex}`}
                                                rehypePlugins={rehypePlugins}
                                                remarkPlugins={[remarkGfm]}
                                                components={markdownComponents as any}
                                            >
                                                {segment.text}
                                            </Markdown>
                                    ))}
                                </div>
                                {!holdResources && guideCardsAfterText.length > 0 && (
                                    <div className='w-full' data-testid='chat-message-guide-cards'>
                                        <ResourceDetails resources={guideCardsAfterText} isLastMessage={isLastMessage} pendingRequest={pendingRequest} animateIn={animateResources} />
                                    </div>
                                )}
                            </StreamingResponse>
                        </div>
                    </div>
                    {/* Ressourcen stehen unter der Antwort, aber in **deren**
                        Spalte: gleiche Zeilengeometrie (30px Seitenrand) und
                        dieselbe Breite, damit sie nicht daneben ausbrechen. */}
                    {!holdResources && message.resources && message.resources.filter((resource: any) => resource.type === 'file').length > 0 && (
                        <div className={resourceRowClass}>
                            <div className={`${resourceColumnClass} mb-2 flex flex-wrap gap-2`}>
                                {message.resources.filter((resource: any) => resource.type === 'file').map((resource: any, idx: number) => (
                                    <FileResource key={idx} resource={resource} resourceSelected={resourceSelected} />
                                ))}
                            </div>
                        </div>
                    )}
                    {(() => {
                        const nonSourceNonFileResources = message.resources?.filter(
                            (resource: any) => !isSourceResource(resource) && resource.type !== 'file'
                        ) || [];
                        
                        if (holdResources || nonSourceNonFileResources.length === 0) {
                            return null;
                        }
                        
                        const resourcesToRenderAtEnd = nonSourceNonFileResources.filter(
                            (resource: any) => !GUIDE_CARD_TYPES.has(resource.type) && !isRenderedInline(resource)
                        );
                        
                        
                        if (resourcesToRenderAtEnd.length === 0) {
                            return null;
                        }
                        
                        return <div className={resourceRowClass}>
                            <div className={resourceColumnClass}>
                                <ResourceDetails resources={resourcesToRenderAtEnd} resourceSelected={resourceSelected} onSubmitInput={onSubmitInput} isLastMessage={isLastMessage} pendingRequest={pendingRequest} animateIn={animateResources} />
                            </div>
                        </div>;
                    })()}
                    {isLastMessage && message.confirmation && (
                        <div className='flex justify-end'>
                            <Button type='button' data-testid='chat-message-confirm' onClick={() => onSubmitInput('yes')}>Yes</Button>
                        </div>
                    )}
                </Message>
                : null;

        case 'botSuggestion':
            return (
                <Message from='assistant' animateIn={animateIn} className='block w-full'>
                    <div className='flex items-start overflow-x-hidden px-[30px] max-sm:px-1'>
                        <BotAvatar />
                        <div className='ml-2 max-w-[80%] overflow-x-hidden rounded-md bg-secondary p-3 text-secondary-foreground max-sm:ml-1 max-sm:max-w-[94%]'>
                            {message.date && (
                                <div className='mb-1 text-[11px] text-muted-foreground'>
                                    {formatBerlin(message.date, t('global.dateShort'))} {formatBerlin(message.date, 'HH:mm')}
                                </div>
                            )}
                            {message.content}
                        </div>
                    </div>
                    {message.resources && message.resources.filter((resource: any) => resource.type === 'file').length > 0 && (
                        <div className='mb-2 flex flex-wrap gap-2'>
                            {message.resources.filter((resource: any) => resource.type === 'file').map((resource: any, idx: number) => (
                                <FileResource key={idx} resource={resource} resourceSelected={resourceSelected} />
                            ))}
                        </div>
                    )}
                    {message.resources && <ResourceDetails resources={message.resources.filter((resource: any) => resource.type !== 'file')} onSubmitInput={onSubmitInput} isLastMessage={isLastMessage} pendingRequest={pendingRequest} />}
                    {isLastMessage && (
                        <div className='flex justify-end'>
                            <Button type='button' data-testid='chat-suggestion-confirm' onClick={() => onSubmitInput('yes')}>Yes</Button>
                        </div>
                    )}
                </Message>
            );

        case 'userMessage': {
            const USER_MESSAGE_TRUNCATE_LENGTH = 250;
            const content = message.content || '';
            const isLong = content.length > USER_MESSAGE_TRUNCATE_LENGTH;
            const displayContent = isLong && !userMessageExpanded
                ? truncate(content, { length: USER_MESSAGE_TRUNCATE_LENGTH, separator: /,? +/ })
                : content;
            const lines = displayContent.split('\n');
            const user = message.user ?? (authentication ? { firstName: authentication.firstName, lastName: authentication.lastName, email: authentication.email } : null);
            return (
                <Message
                    from='user'
                    animateIn={animateIn}
                    data-message-index={index}
                    // Die Zeile richtet sich weiter über die bestehende
                    // Innengeometrie (30px Rand, `justify-end`) aus; von
                    // `Message` bleibt die Einblende-Animation und die
                    // Seiten-Zuordnung für die Blase darunter.
                    className='block w-full'
                >
                    {/* Die Frage bleibt eine Blase — rechtsbündig, gekappt, ohne
                        Avatar und ohne Pfeil. Über ihr eine dezente Zeile mit
                        Name und Zeitpunkt. */}
                    <div
                        className={`mb-0 flex items-start justify-end overflow-x-hidden px-[30px] max-sm:px-1 ${index === 0 ? 'mt-4' : 'mt-0'}`}
                    >
                        <MessageBubble
                            variant='soft'
                            animateIn={animateIn}
                            className='max-w-[80%] max-sm:max-w-[94%]'
                        >
                            <MessageHeader className='mb-1 gap-1.5 px-0 text-[10px] leading-normal text-[var(--oa-fg-4)]'>
                                {user != null && (user.firstName || user.lastName || user.email) && (
                                    user.email ? (
                                        <a
                                            href={`mailto:${user.email}`}
                                            className='text-[10px] text-[var(--oa-fg-4)] no-underline transition-colors hover:text-[var(--oa-fg-2)] hover:no-underline'
                                        >
                                            {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                                        </a>
                                    ) : (
                                        <span className='text-[10px]'>
                                            {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                                        </span>
                                    )
                                )}
                                {message.date && (
                                    <>
                                        {user != null && (user.firstName || user.lastName || user.email) && (
                                            <span> · </span>
                                        )}
                                        <span>
                                            {formatBerlin(message.date, t('global.dateShort'))} {formatBerlin(message.date, 'HH:mm')}
                                        </span>
                                    </>
                                )}
                            </MessageHeader>
                            {/* Die Fläche der Blase liegt als eigene Schicht
                                hinter dem Text (`-z-10`) — sie ist es, die beim
                                Absenden aufpoppt. Deshalb wird ihr Ton dort
                                gesetzt, wo sie steht, statt am Textkasten. */}
                            <MessageBubbleContent
                                // 16px like the bot's answer (`.markdown p` in App.sass) — the bubble sat at 14px
                                // and the two sides read as different sizes (28.09.2026).
                                className={`max-w-full overflow-x-hidden break-words rounded-3xl px-4 py-2 text-base leading-6 text-[var(--oa-fg)] [&>span]:bg-[var(--oa-surface-2)]${isLong ? ' cursor-pointer' : ''}`}
                                data-testid='chat-user-message'
                                onClick={isLong ? () => setUserMessageExpanded(prev => !prev) : undefined}
                                role={isLong ? 'button' : undefined}
                                tabIndex={isLong ? 0 : undefined}
                                onKeyDown={isLong ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setUserMessageExpanded(prev => !prev); } } : undefined}
                                title={isLong ? (userMessageExpanded ? t('chatMessages.clickToCollapse', 'Click to collapse') : t('chatMessages.clickToExpand', 'Click to expand')) : undefined}
                            >
                                {lines.map((line: string, lineIndex: number) => (
                                    <span key={lineIndex}>
                                        {line}
                                        {lineIndex < lines.length - 1 && <br />}
                                    </span>
                                ))}
                            </MessageBubbleContent>
                        </MessageBubble>
                    </div>
                </Message>
            );
        }

        default:
            return null;
    }
}; 